import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { PuzzlePiece, makeEdges } from '@/components/games/PuzzleBoard';

/** Image fictive dédiée à la démo — jamais celle du vrai puzzle. */
const DEMO_IMAGE = `data:image/svg+xml,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="500" height="500" viewBox="0 0 500 500">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#0f2744"/>
      <stop offset="55%" stop-color="#1b4332"/>
      <stop offset="100%" stop-color="#081c15"/>
    </linearGradient>
  </defs>
  <rect width="500" height="500" fill="url(#bg)"/>
  <circle cx="100" cy="100" r="55" fill="#f4a261"/>
  <circle cx="400" cy="90" r="40" fill="#457b9d"/>
  <rect x="300" y="280" width="120" height="120" rx="18" fill="#e76f51"/>
  <path d="M30 380 Q250 280 470 400" stroke="#a8dadc" stroke-width="22" fill="none" stroke-linecap="round"/>
  <circle cx="180" cy="320" r="45" fill="#2a9d8f"/>
  <rect x="60" y="200" width="90" height="70" rx="12" fill="#e9c46a"/>
</svg>
`)}`;

const DEMO_SIZE = 5;
const DEMO_COUNT = DEMO_SIZE * DEMO_SIZE;
const DEMO_SEED = 'prysma-puzzle-intro-demo-5x5';
const TAB = 0.2;
/** Les 3 pièces animées (les autres restent dans le bac). */
const DEMO_PIECES = [0, 1, 2];

function clamp(n, a, b) {
  return Math.min(b, Math.max(a, n));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2;
}

/** Place les 25 pièces entièrement dans le bac (origine clampée). */
function initialTrayLayout(trayW, trayH, piecePx) {
  const cols = 5;
  const rows = 5;
  const padX = 6;
  const padTop = 22;
  const padBot = 6;
  const maxLeft = Math.max(padX, trayW - piecePx - padX);
  const maxTop = Math.max(padTop, trayH - piecePx - padBot);
  const cellW = (trayW - padX * 2) / cols;
  const cellH = (trayH - padTop - padBot) / rows;
  const positions = {};
  for (let i = 0; i < DEMO_COUNT; i += 1) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const left = clamp(padX + col * cellW + (cellW - piecePx) / 2, padX, maxLeft);
    const top = clamp(padTop + row * cellH + (cellH - piecePx) / 2, padTop, maxTop);
    let rot = (i % 4) * 90;
    if (i === 1) rot = 180;
    if (i === 0 || i === 2) rot = 0;
    positions[i] = { left, top, rot, z: 1 + (i % 20) };
  }
  return positions;
}

/** Souris fixe (illustrative) — boutons gauche/droit s’allument selon l’action. */
function DemoMouse({ leftPressed = false, rightPressed = false }) {
  return (
    <div className="flex flex-1 min-w-[6.5rem] items-center justify-center self-stretch" aria-hidden>
      <div className="flex flex-col items-center gap-1.5">
        <svg viewBox="0 0 72 110" className="w-[6.375rem] h-[9.75rem] drop-shadow-md" role="img">
          <title>Souris</title>
          {/* Corps */}
          <rect x="8" y="38" width="56" height="64" rx="28" fill="#e8eaed" stroke="#9aa0a6" strokeWidth="2" />
          {/* Bouton gauche */}
          <path
            d="M10 38 h24 v28 a4 4 0 0 1 -4 4 H14 a4 4 0 0 1 -4 -4 Z"
            fill={leftPressed ? '#f76f4b' : '#ffffff'}
            stroke="#9aa0a6"
            strokeWidth="2"
          />
          {/* Bouton droit */}
          <path
            d="M38 38 h24 v28 a4 4 0 0 1 -4 4 H42 a4 4 0 0 1 -4 -4 Z"
            fill={rightPressed ? '#f76f4b' : '#ffffff'}
            stroke="#9aa0a6"
            strokeWidth="2"
          />
          {/* Molette */}
          <rect x="31" y="44" width="10" height="18" rx="5" fill="#bdc1c6" stroke="#9aa0a6" strokeWidth="1.5" />
          {/* Séparation boutons */}
          <line x1="36" y1="38" x2="36" y2="68" stroke="#9aa0a6" strokeWidth="2" />
        </svg>
        <p className="text-xs text-gray-400 text-center leading-tight max-w-[7rem]">
          {rightPressed ? 'Clic droit' : leftPressed ? 'Clic gauche' : 'Souris'}
        </p>
      </div>
    </div>
  );
}

/**
 * Démo visuelle 5×5 (même taille d’encarts que le puzzle réel).
 * Boucle sur 3 pièces jusqu'à Fermer. Seul Fermer est cliquable.
 */
export default function PuzzleIntroDemo({ onClose, elapsedLabel = '00:00' }) {
  const edges = useMemo(() => makeEdges(DEMO_SIZE, DEMO_SEED), []);
  const wrapRef = useRef(null);
  const [panePx, setPanePx] = useState(0);
  const boardPx = panePx || 360;
  const cellPx = boardPx / DEMO_SIZE;
  const placedPx = cellPx * (1 + TAB * 2);
  const placedOff = cellPx * TAB;

  const [slots, setSlots] = useState(() => Array.from({ length: DEMO_COUNT }, () => null));
  const [hiddenTray, setHiddenTray] = useState(() => new Set());
  const [trayPos, setTrayPos] = useState(() => initialTrayLayout(360, 360, (360 / DEMO_SIZE) * (1 + TAB * 2)));
  const [flying, setFlying] = useState(null);
  const [hint, setHint] = useState(false);
  const [mouseLeft, setMouseLeft] = useState(false);
  const [mouseRight, setMouseRight] = useState(false);

  const boardRef = useRef(null);
  const trayRef = useRef(null);
  const flyingRef = useRef(null);
  const cancelledRef = useRef(false);
  const trayPosRef = useRef(trayPos);
  const metricsRef = useRef({ cellPx, placedPx, placedOff, boardPx });

  trayPosRef.current = trayPos;
  metricsRef.current = { cellPx, placedPx, placedOff, boardPx };

  const [animReady, setAnimReady] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.clientWidth;
      const stacked = typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
      const gap = 16;
      const dialog = el.closest('[data-game-season-dialog]');
      const scroller = dialog?.querySelector('.overflow-y-auto') || el.closest('.overflow-y-auto');
      const bottom = scroller ? scroller.getBoundingClientRect().bottom : window.innerHeight - 16;
      const top = el.getBoundingClientRect().top;
      const availH = Math.max(220, bottom - top - 8);
      const fromW = stacked ? w : (w - gap) / 2;
      const next = Math.max(220, Math.floor(Math.min(fromW, availH)));
      setPanePx((prev) => (Math.abs(prev - next) <= 2 ? prev : next));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    if (!panePx) return;
    const piecePx = (panePx / DEMO_SIZE) * (1 + TAB * 2);
    setTrayPos(initialTrayLayout(panePx, panePx, piecePx));
    setAnimReady(true);
  }, [panePx]);

  useEffect(() => {
    flyingRef.current = flying;
  }, [flying]);

  // Une seule boucle pour toute la durée de la démo (pas de restart au resize)
  useEffect(() => {
    if (!animReady) return undefined;
    cancelledRef.current = false;
    const timers = [];
    let rafId = 0;

    const wait = (ms) => new Promise((resolve) => {
      const id = window.setTimeout(resolve, ms);
      timers.push(id);
    });

    const slotOrigin = (slot) => {
      const { placedOff: off, cellPx: c } = metricsRef.current;
      const cell = boardRef.current?.querySelector?.(`[data-demo-slot="${slot}"]`);
      if (cell) {
        const r = cell.getBoundingClientRect();
        return { x: r.left - off, y: r.top - off };
      }
      const board = boardRef.current?.getBoundingClientRect();
      if (!board) return { x: 0, y: 0 };
      const col = slot % DEMO_SIZE;
      const row = Math.floor(slot / DEMO_SIZE);
      return {
        x: board.left + col * c - off,
        y: board.top + row * c - off,
      };
    };

    const trayOrigin = (piece) => {
      const tray = trayRef.current?.getBoundingClientRect();
      const pos = trayPosRef.current[piece];
      if (!tray || !pos) return { x: 0, y: 0, rot: 0 };
      return {
        x: tray.left + pos.left,
        y: tray.top + pos.top,
        rot: pos.rot || 0,
      };
    };

    /** Interpolation jusqu’à la cible — ne s’arrête jamais au milieu (sauf démontage). */
    const animateTo = (toX, toY, ms) => new Promise((resolve) => {
      const from = flyingRef.current;
      if (!from) {
        resolve();
        return;
      }
      const x0 = from.x;
      const y0 = from.y;
      const rot0 = from.rot || 0;
      const start = performance.now();
      const tick = (now) => {
        if (cancelledRef.current) {
          resolve();
          return;
        }
        const raw = clamp((now - start) / Math.max(1, ms), 0, 1);
        const t = easeInOut(raw);
        const atEnd = raw >= 1;
        const next = {
          piece: from.piece,
          x: atEnd ? toX : lerp(x0, toX, t),
          y: atEnd ? toY : lerp(y0, toY, t),
          rot: flyingRef.current?.rot ?? rot0,
          w: metricsRef.current.placedPx,
          h: metricsRef.current.placedPx,
        };
        flyingRef.current = next;
        setFlying(next);
        if (!atEnd) rafId = requestAnimationFrame(tick);
        else resolve();
      };
      rafId = requestAnimationFrame(tick);
    });

    const pick = (piece) => {
      const { placedPx: px } = metricsRef.current;
      const start = trayOrigin(piece);
      const next = {
        piece,
        x: start.x,
        y: start.y,
        rot: start.rot,
        w: px,
        h: px,
      };
      flyingRef.current = next;
      setFlying(next);
      setHiddenTray((prev) => new Set([...prev, piece]));
      setMouseLeft(true);
      setMouseRight(false);
    };

    const rotateFlying = async () => {
      const cur = flyingRef.current;
      if (!cur) return;
      // Clic gauche reste enfoncé (la pièce est toujours tenue) + clic droit pour pivoter
      setMouseLeft(true);
      setMouseRight(true);
      const next = { ...cur, rot: ((cur.rot || 0) + 90) % 360 };
      flyingRef.current = next;
      setFlying(next);
      await wait(220);
      setMouseRight(false);
      await wait(80);
    };

    const place = (piece, slot) => {
      setSlots((prev) => {
        const next = [...prev];
        next[slot] = piece;
        return next;
      });
      flyingRef.current = null;
      setFlying(null);
      setHint(false);
      setMouseLeft(false);
      setMouseRight(false);
    };

    const resetAll = () => {
      setSlots(Array.from({ length: DEMO_COUNT }, () => null));
      setHiddenTray(new Set());
      flyingRef.current = null;
      setFlying(null);
      setHint(false);
      setMouseLeft(false);
      setMouseRight(false);
    };

    /** Glisse sans interruption jusqu’à la case, puis pose. */
    const slideToSlotAndPlace = async (piece, slot, moveMs) => {
      if (cancelledRef.current) return;
      setMouseLeft(true);
      setMouseRight(false);
      const dest = slotOrigin(slot);
      await animateTo(dest.x, dest.y, moveMs);
      if (cancelledRef.current) return;
      const exact = slotOrigin(slot);
      const cur = flyingRef.current;
      if (cur) {
        const snapped = {
          ...cur,
          x: exact.x,
          y: exact.y,
          rot: 0,
          w: metricsRef.current.placedPx,
          h: metricsRef.current.placedPx,
        };
        flyingRef.current = snapped;
        setFlying(snapped);
      }
      await wait(320);
      if (cancelledRef.current) return;
      place(piece, slot);
    };

    const loopOnce = async () => {
      const [p0, p1, p2] = DEMO_PIECES;

      // Pièce 0 — bac → case (clic gauche maintenu)
      await wait(500);
      if (cancelledRef.current) return;
      pick(p0);
      await wait(180);
      await slideToSlotAndPlace(p0, p0, 1200);

      // Pièce 1 — déplacement, relâche, pivots clic droit, puis pose
      await wait(400);
      if (cancelledRef.current) return;
      pick(p1);
      await wait(180);
      {
        const dest = slotOrigin(p1);
        await animateTo(dest.x, dest.y - 36, 1100);
        if (cancelledRef.current) return;
        setMouseLeft(true);
        setHint(true);
        await wait(450);
        await rotateFlying();
        await wait(280);
        await rotateFlying();
        await wait(250);
        setHint(false);
        await wait(80);
        await slideToSlotAndPlace(p1, p1, 550);
      }

      // Pièce 2 — bac → case (clic gauche maintenu)
      await wait(400);
      if (cancelledRef.current) return;
      pick(p2);
      await wait(180);
      await slideToSlotAndPlace(p2, p2, 1200);

      await wait(1200);
      if (cancelledRef.current) return;
      resetAll();
      await wait(400);
    };

    const run = async () => {
      await wait(200);
      while (!cancelledRef.current) {
        await loopOnce();
      }
    };
    run();

    return () => {
      cancelledRef.current = true;
      timers.forEach((id) => window.clearTimeout(id));
      if (rafId) cancelAnimationFrame(rafId);
      flyingRef.current = null;
      setFlying(null);
      setMouseLeft(false);
      setMouseRight(false);
    };
  }, [animReady]);

  const hintPos = flying
    ? { x: flying.x + placedPx / 2, y: flying.y - 10 }
    : { x: 0, y: 0 };

  return (
    <div
      className="relative rounded-md border border-orange-300/60 dark:border-orange-800/60 bg-white/95 dark:bg-gray-950/95 p-3"
      role="dialog"
      aria-label="Démonstration du puzzle"
    >
      <div className="relative flex items-center justify-center mb-2 min-h-[2rem]">
        <p className="text-lg font-semibold uppercase tracking-wide text-[#f76f4b]">Démo</p>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 shrink-0 rounded-md bg-[#f76f4b] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#e85f3a]"
        >
          <X size={14} />
          Fermer
        </button>
      </div>

      <p className="text-sm font-mono font-bold text-[#f76f4b] mb-2 tabular-nums">{elapsedLabel}</p>

      <div
        ref={wrapRef}
        className="pointer-events-none select-none w-full min-w-0 flex flex-col sm:flex-row gap-4 items-stretch"
      >
        <div ref={boardRef} className="relative shrink-0" style={{ width: boardPx, height: boardPx }}>
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${DEMO_SIZE}, minmax(0, 1fr))`,
              width: boardPx,
              height: boardPx,
            }}
          >
            {slots.map((piece, i) => (
              <div
                key={`demo-slot-${i}`}
                data-demo-slot={i}
                className="relative aspect-square bg-gray-800/70 overflow-visible"
              >
                {piece != null ? (
                  <PuzzlePiece
                    piece={piece}
                    size={DEMO_SIZE}
                    image={DEMO_IMAGE}
                    edges={edges}
                    clipKey="demo-slot"
                    className="absolute z-10"
                    style={{
                      left: -placedOff,
                      top: -placedOff,
                      width: placedPx,
                      height: placedPx,
                    }}
                  />
                ) : null}
              </div>
            ))}
          </div>
        </div>

        <div
          ref={trayRef}
          className="relative shrink-0 rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/40 dark:bg-gray-900/40 overflow-hidden"
          style={{ width: boardPx, height: boardPx }}
        >
          <p className="absolute left-2 top-1 z-50 text-[10px] uppercase tracking-wide text-gray-400">Pièces</p>
          {Array.from({ length: DEMO_COUNT }, (_, piece) => {
            const pos = trayPos[piece];
            if (!pos || hiddenTray.has(piece) || slots[piece] === piece) return null;
            return (
              <PuzzlePiece
                key={`demo-tray-${piece}`}
                piece={piece}
                size={DEMO_SIZE}
                image={DEMO_IMAGE}
                edges={edges}
                clipKey="demo-tray"
                rot={pos.rot}
                className="absolute drop-shadow-md"
                style={{
                  width: placedPx,
                  height: placedPx,
                  left: pos.left,
                  top: pos.top,
                  zIndex: pos.z,
                }}
              />
            );
          })}
        </div>

        <DemoMouse leftPressed={mouseLeft} rightPressed={mouseRight} />
      </div>

      {/* Portal body : le Dialog a un transform, sinon fixed est faux et la pièce « téléporte ». */}
      {flying && typeof document !== 'undefined'
        ? createPortal(
          <div
            className="fixed pointer-events-none z-[35000] drop-shadow-xl"
            style={{
              left: flying.x,
              top: flying.y,
              width: flying.w,
              height: flying.h,
            }}
          >
            <PuzzlePiece
              piece={flying.piece}
              size={DEMO_SIZE}
              image={DEMO_IMAGE}
              edges={edges}
              clipKey="demo-fly"
              rot={flying.rot}
              className="w-full h-full"
            />
          </div>,
          document.body,
        )
        : null}

      {hint && flying && typeof document !== 'undefined'
        ? createPortal(
          <div
            className="fixed z-[35001] pointer-events-none -translate-x-1/2 -translate-y-full"
            style={{ left: hintPos.x, top: hintPos.y }}
          >
            <div className="rounded-md bg-black/85 text-white text-xs font-semibold px-2.5 py-1.5 shadow-lg whitespace-nowrap border border-white/20">
              Clic droit pour pivoter
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}
